import React, { useState } from "react";
import { TListingValuationTabOutput, TValuationTabSignal } from "../../../../domain/codecs/formIO/ListingValuationTabFormIO";
import { penceToCopyText, snakeCaseToCopyText } from "../../../../shared/src/util";
import { TPercentageSize } from "../../models/StringLiterals";
import { CRMCardOutsideLabelled } from "../CRMCardOutsideLabelled/CRMCardOutsideLabelled";
import { CRMCollapsibleSectionComponent } from "../CRMCollapsibleSectionComponent/CRMCollapsibleSectionComponent";
import { SpacingColumn } from "../BuildingBlocks/SpacingColumn";
import { SpacingRow } from "../BuildingBlocks/SpacingRow";
import { Background } from "../BuildingBlocks/Background";
import { Text } from "../BuildingBlocks/Text";
import { Padding } from "../BuildingBlocks/Padding";
import { WeightBold } from "../WeightBold/WeightBold";
import { CRMSpacing } from "../../models/CRMSpacing";
import { CRMColors } from "../../models/CRMColors";
import { CRMFontSizes } from "../../models/CRMFontSizes";

const SIGNAL_LABELS: Record<string, string> = {
    last_sold_plus_hpi: "Last sale price, adjusted for the local market",
    comps_price_per_sqm: "Price per square metre of similar homes nearby",
    comps_average: "Average of nearby sales",
    chimnie_avm: "Automated computer estimate",
    partner_price: "Partner's price",
};

const signalLabel = (kind: string): string =>
    SIGNAL_LABELS[kind] ?? snakeCaseToCopyText(kind);

const plainReason = (output: TListingValuationTabOutput): string => {
    const parts: Array<string> = [];

    if (output.comps_count !== null && output.comps_count > 0) {
        parts.push(`${output.comps_count} recent sales near the property`);
    }
    if (output.signals.some((signal) => signal.kind === "chimnie_avm")) {
        parts.push("an automated computer estimate");
    }
    if (output.signals.some((signal) => signal.kind === "last_sold_plus_hpi")) {
        parts.push("what this property last sold for");
    }

    if (parts.length === 0) {
        return "No breakdown was recorded for this price.";
    }

    const list = parts.length === 1
        ? parts[0]
        : `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}`;

    return `We weighed up ${list}${
        output.hpi_uplift_pct === null ? "" : ", allowing for how prices have moved locally"
    }.`;
};

const highestSignal = (signals: Array<TValuationTabSignal>): TValuationTabSignal | null =>
    signals.reduce<TValuationTabSignal | null>(
        (highest, signal) =>
            highest === null || signal.value_pence > highest.value_pence ? signal : highest,
        null,
    );

const barPercentage = (weight: number, heaviestWeight: number): TPercentageSize =>
    heaviestWeight <= 0 ? "0%" : `${Math.round((weight / heaviestWeight) * 100)}%`;

const SignalRow = (props: { signal: TValuationTabSignal; heaviestWeight: number }): JSX.Element => (
    <SpacingRow spacing={CRMSpacing.SMALL} childSize="1fr 80px 90px" alignItems="center">
        <Text size={CRMFontSizes.X_SMALL}>{signalLabel(props.signal.kind)}</Text>
        <Background color={CRMColors.NEUTRAL_12} height="8px" borderRadius="4px">
            <Background
                color={CRMColors.PRIMARY_4}
                width={barPercentage(props.signal.weight, props.heaviestWeight)}
                height="8px"
                borderRadius="4px"
            />
        </Background>
        <Text size={CRMFontSizes.X_SMALL} align="right" color={CRMColors.NEUTRAL_2}>
            {penceToCopyText(props.signal.value_pence)}
        </Text>
    </SpacingRow>
);

export const CRMValuationReasonCard = (props: { output: TListingValuationTabOutput }): JSX.Element => {
    const { output } = props;
    const [workingsAreOpen, setWorkingsAreOpen] = useState<boolean>(false);

    if (output.market_price_pence === null) {
        return <></>;
    }

    const heaviest = output.signals.reduce(
        (weight, signal) => signal.weight > weight ? signal.weight : weight,
        0,
    );
    const highest = highestSignal(output.signals);

    const wasAmended = output.amended_reason !== null;
    const hasEvidence = output.signals.length > 0
        || (output.comps_count !== null && output.comps_count > 0);

    return (
        <CRMCardOutsideLabelled labelColor="primary-6">
            <Padding spacing={CRMSpacing.MEDIUM}>
                <SpacingColumn spacing={CRMSpacing.SMALL}>
                    <Text size={CRMFontSizes.MED_PLUS}><WeightBold>Why this price</WeightBold></Text>

                    {output.amended_reason !== null &&
                        <SpacingColumn spacing={CRMSpacing.TINY}>
                            <Text size={CRMFontSizes.X_SMALL} color={CRMColors.NEUTRAL_4}>
                                SET BY HAND, NOT BY THE ENGINE
                            </Text>
                            <Text size={CRMFontSizes.SMALL}>{output.amended_reason}</Text>
                        </SpacingColumn>
                    }

                    {wasAmended && hasEvidence &&
                        <Text size={CRMFontSizes.X_SMALL} color={CRMColors.NEUTRAL_4}>
                            WHAT THE EVIDENCE SAID
                        </Text>
                    }

                    {/* Suppressed entirely on a hand-set price with nothing behind it:
                        the valuer's own reason is directly above, and "no breakdown was
                        recorded" adds nothing except doubt about the number they just
                        entered deliberately. */}
                    {(hasEvidence || !wasAmended) &&
                        <Text size={CRMFontSizes.SMALL} color={CRMColors.NEUTRAL_2}>
                            {plainReason(output)}
                        </Text>
                    }

                    {output.clip_applied !== null && highest !== null &&
                        <Text size={CRMFontSizes.SMALL} color={CRMColors.NEUTRAL_2}>
                            {`Why it isn't higher: the ${signalLabel(highest.kind).toLowerCase()} came out at `}
                            {penceToCopyText(highest.value_pence)}
                            {", but we trusted it less because it disagreed with the rest of the evidence."}
                        </Text>
                    }

                    {output.signals.length > 0 &&
                        <CRMCollapsibleSectionComponent
                            title="Show the workings"
                            isOpen={workingsAreOpen}
                            onOpenChange={() => setWorkingsAreOpen(!workingsAreOpen)}
                        >
                            <Padding type="vertical" spacing={CRMSpacing.SMALL}>
                                <SpacingColumn spacing={CRMSpacing.SMALL}>
                                    {output.signals.map((signal) =>
                                        <SignalRow
                                            key={signal.kind}
                                            signal={signal}
                                            heaviestWeight={heaviest}
                                        />,
                                    )}
                                    {output.clip_applied !== null &&
                                        <Text size={CRMFontSizes.X_SMALL} color={CRMColors.NEUTRAL_4}>
                                            {output.clip_applied}
                                        </Text>
                                    }
                                    {output.hpi_uplift_pct !== null &&
                                        <Text size={CRMFontSizes.X_SMALL} color={CRMColors.NEUTRAL_4}>
                                            {`Local market adjustment: ${output.hpi_uplift_pct > 0 ? "+" : ""}${output.hpi_uplift_pct.toFixed(1)}%`}
                                            {output.hpi_fell_back_to_national
                                                ? " (no figures for this area, so national ones were used)"
                                                : ""
                                            }
                                        </Text>
                                    }
                                </SpacingColumn>
                            </Padding>
                        </CRMCollapsibleSectionComponent>
                    }
                </SpacingColumn>
            </Padding>
        </CRMCardOutsideLabelled>
    );
};
